import { Button } from '@creditornot/cb-button';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { SegmentedControl } from '@creditornot/cb-segmented-control';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { WoltModal } from 'common/components';
import { useOfferingMenuFeaturesContext } from 'modules/shared-menu/contexts/OfferingMenuFeaturesContext';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { getTypedKeys } from 'modules/shared-menu/utils';

import { ColumnSelector, GenericColumn } from './DataTable/ColumnSelector';
import { ALL_CATEGORY_COLUMNS, ALL_OFFER_COLUMNS, ALL_OPTION_COLUMNS, tableView } from './DataTable/constants';
import { DataTableView } from './DataTable/useDataTable';
import { buildNonAllowedFields } from '../utils';

type ColumnsToDisplay = Record<DataTableView, GenericColumn[]>;

type Props = {
  onClose: () => void;
  onConfirm: (columnsToDisplay: ColumnsToDisplay) => void;
};

const StyledWoltModal = styled(WoltModal)`
  background: ${cssThemedValue('surfaceMain')};
  color: ${cssThemedValue('textPrimary')};
  border-radius: 8px;
`;

const StyledModalContent = styled(ModalContent)`
  overflow: auto;
  padding: 0;
  border-collapse: collapse;
  height: 370px;
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const ModeToggleContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 8px;
  justify-content: center;
`;

export const ColumnsPickerModal = ({ onClose, onConfirm }: Props) => {
  const [currentView, setCurrentView] = useState<DataTableView>(tableView.OFFERS);
  const menuFeatures = useOfferingMenuFeaturesContext();
  const menuPermissions = useOfferingMenuPermissionsContext();

  const nonAllowedFields = useMemo(
    () => buildNonAllowedFields(menuFeatures, menuPermissions),
    [menuFeatures, menuPermissions],
  );

  const allColumnsWithNonAllowedFieldsDisabled = useMemo(
    () => ({
      offers: ALL_OFFER_COLUMNS.map(field => {
        const fieldShouldBeDisabled = nonAllowedFields.offers.includes(field.value);
        return fieldShouldBeDisabled ? { ...field, disabled: true } : field;
      }),
      options: ALL_OPTION_COLUMNS.map(field => {
        const fieldShouldBeDisabled = nonAllowedFields.options.includes(field.value);
        return fieldShouldBeDisabled ? { ...field, disabled: true } : field;
      }),
      categories: ALL_CATEGORY_COLUMNS.map(field => {
        const fieldShouldBeDisabled = nonAllowedFields.categories.includes(field.value);
        return fieldShouldBeDisabled ? { ...field, disabled: true } : field;
      }),
    }),
    [nonAllowedFields],
  );

  const [selectedColumns, setSelectedColumns] = useState<ColumnsToDisplay>({
    offers: allColumnsWithNonAllowedFieldsDisabled.offers.filter(
      ({ defaultChecked, disabled }) => defaultChecked && !disabled,
    ),
    options: allColumnsWithNonAllowedFieldsDisabled.options.filter(
      ({ defaultChecked, disabled }) => defaultChecked && !disabled,
    ),
    categories: allColumnsWithNonAllowedFieldsDisabled.categories.filter(
      ({ defaultChecked, disabled }) => defaultChecked && !disabled,
    ),
  });

  const areAllColumnsDeselected = useMemo(
    () => getTypedKeys(selectedColumns).every(entityName => selectedColumns[entityName].length === 0),
    [selectedColumns],
  );

  return (
    <StyledWoltModal isOpen onRequestClose={onClose}>
      <ModalHeader onCloseClick={onClose}>
        <FormattedMessage id="shared-menu.editor.importer.columns-picker-modal.title" />
      </ModalHeader>
      <ModeToggleContainer>
        <SegmentedControl<DataTableView>
          options={[
            { label: 'Offers', value: tableView.OFFERS },
            { label: 'Options', value: tableView.OPTIONS },
            { label: 'Categories', value: tableView.CATEGORIES },
          ]}
          value={currentView}
          onChange={setCurrentView}
        />
      </ModeToggleContainer>

      <StyledModalContent>
        <ColumnSelector
          variant="blue"
          actionsPlacement="top"
          options={allColumnsWithNonAllowedFieldsDisabled[currentView]}
          selectedColumns={selectedColumns[currentView]}
          onChange={selectedColumns => {
            setSelectedColumns(prevState => ({
              ...prevState,
              [currentView]: selectedColumns,
            }));
          }}
          onSelectAll={() => {
            setSelectedColumns(prevState => ({
              ...prevState,
              [currentView]: allColumnsWithNonAllowedFieldsDisabled[currentView].filter(({ disabled }) => !disabled),
            }));
          }}
          onDeselectAll={() => {
            setSelectedColumns(prevState => ({
              ...prevState,
              [currentView]: [],
            }));
          }}
        />
      </StyledModalContent>
      <StyledModalFooter>
        <Button size="small" variant="lightBlue" onClick={onClose}>
          <FormattedMessage id="state.cancel" />
        </Button>
        <Button
          size="small"
          variant="blue"
          onClick={() => {
            onConfirm(selectedColumns);
          }}
          disabled={areAllColumnsDeselected}
        >
          <FormattedMessage id="state.confirm" />
        </Button>
      </StyledModalFooter>
    </StyledWoltModal>
  );
};
